import { useState } from 'react';
import { Pin } from 'lucide-react';

const TYPE_LABELS: Record<string, string> = {
  expense: 'Expense',
  reminder: 'Reminder',
  task: 'Task',
  meeting_note: 'Meeting Note',
  decision: 'Decision',
  follow_up: 'Follow-up',
  opportunity: 'Opportunity',
  risk: 'Risk',
  note: 'Note',
  relationship_touchpoint: 'Relationship',
};

interface Props {
  items: any[];
  onConfirmAll: (items: any[]) => void;
  onClose: () => void;
  saving?: boolean;
}

export function CaptureConfirmCards({ items, onConfirmAll, onClose, saving = false }: Props) {
  const [checked, setChecked] = useState<Set<number>>(
    () => new Set(items.map((_, i) => i))
  );

  const toggle = (i: number) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const checkedItems = items.filter((_, i) => checked.has(i));

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        {items.map((item, i) => (
          <div
            key={i}
            onClick={() => toggle(i)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: checked.has(i) ? 'var(--card)' : 'var(--bg)',
              border: `1px solid ${checked.has(i) ? 'var(--border)' : 'transparent'}`,
              borderRadius: '12px',
              padding: '12px',
              marginBottom: '8px',
              cursor: 'pointer',
              opacity: checked.has(i) ? 1 : 0.5,
              transition: 'all 0.15s',
            }}
          >
            <span style={{ flexShrink: 0, display: 'inline-flex' }}>{item.icon ? <span style={{ fontSize: 22 }}>{item.icon}</span> : <Pin size={20} strokeWidth={1.8} color="var(--muted)" />}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                color: 'var(--muted)',
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                margin: '0 0 2px',
              }}>
                {TYPE_LABELS[item.type] || item.type}
              </p>
              <p style={{
                color: 'var(--gold)',
                fontSize: '13px',
                fontWeight: 600,
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {item.display_label}
              </p>
            </div>
            <div style={{
              width: '20px',
              height: '20px',
              borderRadius: '6px',
              border: `2px solid ${checked.has(i) ? 'var(--gold)' : 'var(--border)'}`,
              background: checked.has(i) ? 'var(--gold)' : 'transparent',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {checked.has(i) && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => onConfirmAll(checkedItems)}
        disabled={saving || checkedItems.length === 0}
        style={{
          width: '100%',
          padding: '14px',
          background: checkedItems.length > 0 ? 'var(--gold)' : 'var(--border)',
          color: checkedItems.length > 0 ? '#000' : 'var(--muted)',
          fontWeight: 700,
          fontSize: '15px',
          border: 'none',
          borderRadius: '12px',
          cursor: checkedItems.length > 0 && !saving ? 'pointer' : 'not-allowed',
          marginBottom: '8px',
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        {saving ? (
          <>
            <div style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid #000', borderTopColor: 'transparent', animation: 'ccSpin 0.8s linear infinite' }} />
            Saving...
          </>
        ) : (
          `Confirm ${checkedItems.length} item${checkedItems.length !== 1 ? 's' : ''}`
        )}
      </button>

      <button
        onClick={onClose}
        style={{
          width: '100%',
          padding: '10px',
          background: 'var(--bg)',
          color: 'var(--muted)',
          fontSize: '13px',
          border: '1px solid var(--border)',
          borderRadius: '10px',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Cancel
      </button>

      <style>{`@keyframes ccSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default CaptureConfirmCards;
